import React from 'react';
import { useAppSelector } from 'store/hook';
import CombinedFourWheels from 'views/ControlRoom/components/Schematics/CombinedFourWheels';
import CombinedSixWheels from 'views/ControlRoom/components/Schematics/CombinedSixWheels';
import CombinedFourSixWheels from 'views/ControlRoom/components/Schematics/CombinedFourSixWheels';
import CombinedThreeAxlesSixWheels from 'views/ControlRoom/components/Schematics/CombinedThreeAxlesSixWheels';
import CombinedThreeAxlesTenWheels from 'views/ControlRoom/components/Schematics/CombinedThreeAxlesTenWheels';
import {
  getDynamicAssetTireInfo,
  getOrderedTireCodes,
  getTireLimits,
  getTireValuesFromSensorData,
  getLastUpdatedTimestamp,
  getTirePositionKey,
} from 'views/TirePressure/util';
import { determineTireStatus, TireStatus } from 'utils/tpms';
import { convertKpaToPsi } from 'utils/helpers/unitSystem';
import { type Asset } from 'types/asset.types';
import { useTranslation } from 'react-i18next';

interface Props {
  asset: Asset;
  liveTagData: Record<string, any>;
  width?: number;
  maxHeight?: number;
}

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 7,
  fontWeight: 800,
  color: '#94a3b8',
  letterSpacing: '0.05em',
  lineHeight: 1,
};

const TirePressureSchematic: React.FC<Props> = ({
  asset,
  liveTagData,
  width = 70,
  maxHeight,
}) => {
  const { t } = useTranslation();
  const { limits, batteryData } = useAppSelector(
    (state) => state.tireProfileReducer
  );
  const makeModelDetails = useAppSelector(
    (state) => state.makeModelDetailsReducer
  );
  const tirePatterns = useAppSelector((state) => state.tirePatternReducer);
  const unitSystem = useAppSelector((state) => state.persistedReducer).customer
    .unitSystem;
  const { id: customerId } = useAppSelector(
    (state) => state.persistedReducer
  ).customer;

  if (!asset?.tpmsEnabled) return null;
  if (!asset.device?.deviceId) return null;

  // Extract pressure and temperature data from liveTagData
  const pressureRaw: any = {};
  const temperatureRaw: any = {};
  const dataKeys: string[] = [];

  Object.entries(liveTagData || {}).forEach(([key, value]) => {
    const match = key.match(
      /^tpms_tire_(0x[0-9a-fA-F]+)_(pressure|temperature)(_value)?$/
    );
    if (match) {
      const tireCode = match[1];
      const metric = match[2];
      if (!dataKeys.includes(tireCode)) {
        dataKeys.push(tireCode);
      }
      if (metric === 'pressure') {
        const tsKey = `tpms_tire_${tireCode}_pressure_ts`;
        pressureRaw[tireCode] = {
          value,
          ts: Number(liveTagData[tsKey]),
          device_sensor: tireCode,
        };
      } else if (metric === 'temperature') {
        const tsKey = `tpms_tire_${tireCode}_temperature_ts`;
        temperatureRaw[tireCode] = {
          value,
          ts: Number(liveTagData[tsKey]),
          device_sensor: tireCode,
        };
      }
    }
  });

  const assetTireDetails = getDynamicAssetTireInfo(
    makeModelDetails,
    tirePatterns.tirePatternPositions,
    asset.make,
    asset.model,
    customerId
  );

  const itemPattern = asset.patternName ?? assetTireDetails.patternName;
  const { tireCodes } = assetTireDetails;

  const currentDataKeys =
    dataKeys.length > 0 ? dataKeys : Object.keys(tireCodes);
  const currentLimit = getTireLimits(asset.make, asset.model, limits);
  const orderedKeys = getOrderedTireCodes(currentDataKeys, itemPattern);

  const pressureValues = getTireValuesFromSensorData(
    orderedKeys,
    Object.values(pressureRaw),
    currentLimit,
    unitSystem,
    asset.make,
    asset.model,
    'pressure'
  );

  const lastUpdated = getLastUpdatedTimestamp(Object.values(pressureRaw));
  const isDataStale = (() => {
    if (lastUpdated === 'N/A') return false;
    const lastUpdateTime = new Date(lastUpdated);
    const now = new Date();
    const hoursDiff =
      (now.getTime() - lastUpdateTime.getTime()) / (1000 * 60 * 60);
    return hoursDiff > 24;
  })();

  const pressureStatusMap: Record<string, TireStatus> = {};
  const temperatureStatusMap: Record<string, TireStatus> = {};
  const tireStaleMap: Record<string, boolean> = {};
  const pressureDisplayValues: Record<string, string | null> = {};
  const temperatureDisplayValues: Record<string, string | null> = {};

  orderedKeys.forEach((key, index) => {
    const tireNumber = index + 1;
    const pKey = `p${tireNumber}`;
    const posKey = getTirePositionKey(key, itemPattern, dataKeys);

    const pItem = pressureRaw[key];
    const rawP = Number(pItem?.value);
    const pressureValue =
      pItem?.value !== undefined && !isNaN(rawP)
        ? unitSystem === 'metric'
          ? rawP
          : convertKpaToPsi(rawP)
        : null;

    const tireLimit = currentLimit.find((l: any) => l.tireCode === key) || {};
    const maxP =
      unitSystem === 'metric'
        ? tireLimit.maxPressure
        : convertKpaToPsi(tireLimit.maxPressure);
    const minP =
      unitSystem === 'metric'
        ? tireLimit.minPressure
        : convertKpaToPsi(tireLimit.minPressure);

    let pStatus: TireStatus = TireStatus.NO_DATA;
    if (pItem?.ts && pressureValue !== null) {
      pStatus = determineTireStatus(
        pressureValue,
        { min: minP, max: maxP },
        true,
        'pressure'
      );
    }
    pressureStatusMap[pKey] = pStatus;

    if (posKey) {
      pressureDisplayValues[posKey] =
        pressureValue !== null ? String(Math.round(pressureValue)) : null;
    }

    const tItem = temperatureRaw[key];
    const rawT = Number(tItem?.value);
    const tempValue = tItem?.value !== undefined && !isNaN(rawT) ? rawT : null;

    let tStatus: TireStatus = TireStatus.NO_DATA;
    if (tItem?.ts && tempValue !== null) {
      tStatus = determineTireStatus(
        tempValue,
        { min: tireLimit.minTemperature, max: tireLimit.maxTemperature },
        true,
        'temperature'
      );
    }
    temperatureStatusMap[pKey] = tStatus;

    if (posKey) {
      temperatureDisplayValues[posKey] =
        tempValue !== null ? String(Math.round(tempValue)) : null;
    }

    const tireTs = pItem?.ts;
    const hoursDiff = tireTs ? (Date.now() - tireTs) / (1000 * 60 * 60) : 0;
    tireStaleMap[pKey] = hoursDiff > 24;

    pressureValues[`${pKey}HasSensor`] = true;
    pressureValues[`${pKey}IsStale`] = tireStaleMap[pKey];
  });
  pressureValues.isDataStale = isDataStale;

  const deviceBattery = batteryData?.[asset.device.deviceId];
  let batteryLowMap: any = {};
  if (itemPattern === '10x6') {
    batteryLowMap = {
      p1: !!deviceBattery?.['0x17']?.isBatteryLow,
      p2: !!deviceBattery?.['0x19']?.isBatteryLow,
      p3: !!deviceBattery?.['0x27']?.isBatteryLow,
      p4: !!deviceBattery?.['0x29']?.isBatteryLow,
      p5: !!deviceBattery?.['0x26']?.isBatteryLow,
      p6: !!deviceBattery?.['0x2A']?.isBatteryLow,
      p7: !!deviceBattery?.['0x37']?.isBatteryLow,
      p8: !!deviceBattery?.['0x39']?.isBatteryLow,
      p9: !!deviceBattery?.['0x36']?.isBatteryLow,
      p10: !!deviceBattery?.['0x3A']?.isBatteryLow,
    };
  } else if (itemPattern === '6x6') {
    batteryLowMap = {
      p1: !!deviceBattery?.['0x17']?.isBatteryLow,
      p2: !!deviceBattery?.['0x19']?.isBatteryLow,
      p3: !!deviceBattery?.['0x27']?.isBatteryLow,
      p4: !!deviceBattery?.['0x29']?.isBatteryLow,
      p5: !!deviceBattery?.['0x37']?.isBatteryLow,
      p6: !!deviceBattery?.['0x39']?.isBatteryLow,
    };
  } else if (itemPattern === '6x4') {
    batteryLowMap = {
      p1: !!deviceBattery?.['0x17']?.isBatteryLow,
      p2: !!deviceBattery?.['0x19']?.isBatteryLow,
      p3: !!deviceBattery?.['0x27']?.isBatteryLow,
      p4: !!deviceBattery?.['0x29']?.isBatteryLow,
      p5: !!deviceBattery?.['0x26']?.isBatteryLow,
      p6: !!deviceBattery?.['0x2A']?.isBatteryLow,
    };
  } else if (itemPattern === '4x6') {
    batteryLowMap = {
      p1: !!deviceBattery?.['0x16']?.isBatteryLow,
      p2: !!deviceBattery?.['0x17']?.isBatteryLow,
      p3: !!deviceBattery?.['0x19']?.isBatteryLow,
      p4: !!deviceBattery?.['0x1A']?.isBatteryLow,
      p5: !!deviceBattery?.['0x27']?.isBatteryLow,
      p6: !!deviceBattery?.['0x29']?.isBatteryLow,
    };
  } else {
    batteryLowMap = {
      p1: !!deviceBattery?.['0x17']?.isBatteryLow,
      p2: !!deviceBattery?.['0x19']?.isBatteryLow,
      p3: !!deviceBattery?.['0x27']?.isBatteryLow,
      p4: !!deviceBattery?.['0x29']?.isBatteryLow,
    };
  }

  // Combined schematic viewBox internals (width × height):
  //   4-wheel:      100 × 150
  //   6x4:          160 × 200
  //   4x6:          160 × 200
  //   6x6 (3-axle): 100 × 210
  //   10x6 (3-axle, tandem duals): 160 × 210
  // After rotate(90deg) the CSS width maps to visual height and vice versa.
  // We size so that schematicWidth (CSS) = maxHeight, preserving aspect ratio.
  const isDualWide =
    itemPattern === '6x4' || itemPattern === '4x6' || itemPattern === '10x6';
  const internalW = isDualWide ? 160 : 100;
  const internalH =
    itemPattern === '6x4' || itemPattern === '4x6'
      ? 200
      : itemPattern === '6x6' || itemPattern === '10x6'
      ? 210
      : 150;

  let schematicWidth: number;
  let schematicHeight: number;

  // Landscape viewBoxes (dual-tire rows) are wider, so after 90° rotation
  // their visual width is proportionally smaller. Scale up so their visual
  // width matches the 4-wheel schematic.
  const effectiveMaxH =
    maxHeight && isDualWide ? Math.round(maxHeight * (160 / 100)) : maxHeight;

  if (effectiveMaxH) {
    const pxRatio = effectiveMaxH / internalW;
    schematicWidth = Math.round(internalW * pxRatio);
    schematicHeight = Math.round(internalH * pxRatio);
  } else {
    schematicWidth = width;
    schematicHeight = Math.round(width * (internalH / internalW));
  }

  const commonProps = {
    pressureValues: pressureDisplayValues,
    temperatureValues: temperatureDisplayValues,
    pressureStatusMap,
    temperatureStatusMap,
    batteryLow: batteryLowMap,
    tireStaleMap,
    width: schematicWidth,
    height: schematicHeight,
  };

  const legend = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ ...LABEL_STYLE, fontSize: 7 }}>Temperature</span>
      {/* Split tile — after 90° CW rotation: left = temperature, right = pressure */}
      <div
        style={{
          display: 'flex',
          width: 24,
          height: 10,
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.15)',
        }}
      >
        <div style={{ flex: 1, background: '#e89c6a' }} />
        <div
          style={{
            width: 1,
            background: 'rgba(255,255,255,0.25)',
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, background: '#6ea8d8' }} />
      </div>
      <span style={{ ...LABEL_STYLE, fontSize: 7 }}>{t('pressure_3')}</span>
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          transform: 'rotate(90deg)',
          // Negative margins collapse the extra layout space the rotated box occupies
          marginTop: -Math.max(0, (schematicHeight - schematicWidth) / 2),
          marginBottom: -Math.max(0, (schematicHeight - schematicWidth) / 2),
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              transform: 'rotate(-90deg)',
            }}
          >
            <span style={LABEL_STYLE}>▲</span>
            <span style={LABEL_STYLE}>FRONT</span>
          </div>
          {itemPattern === '6x4' ? (
            <CombinedSixWheels {...commonProps} />
          ) : itemPattern === '4x6' ? (
            <CombinedFourSixWheels {...commonProps} />
          ) : itemPattern === '6x6' ? (
            <CombinedThreeAxlesSixWheels {...commonProps} />
          ) : itemPattern === '10x6' ? (
            <CombinedThreeAxlesTenWheels {...commonProps} />
          ) : (
            <CombinedFourWheels {...commonProps} />
          )}
          <span style={{ ...LABEL_STYLE, transform: 'rotate(-90deg)' }}>
            REAR
          </span>
        </div>
      </div>
      {legend}
    </div>
  );
};

export default TirePressureSchematic;
